import React, { Suspense, useEffect, useMemo, useRef, useState } from 'react'
import useQueryGetUserDetail from '@/api/auth/getUser.ts'
import useQueryGetUserRecentState from '@/api/user/getUserRecentState.ts'
import { useNavigate } from 'react-router-dom'
import BiometricDashboard from '@/pages/users/detail/BiometricDashboard.tsx'
import UserDetailMap from '@/pages/users/UserDetailMap.tsx'
import { format } from 'date-fns'
import { calculateAge } from '@/util/person.ts'
import ContactBadge from '@/component/badge/ContactBadge.tsx'
import useQueryGetUserStatAverage from '@/api/user/getUserStatAverage.ts'
import AlertStatusBadge from '@/component/badge/AlertStatusBadge.tsx'
import BatteryStatusBadge from '@/component/badge/BatteryStatusBadge.tsx'
import { useTranslation } from 'react-i18next'
import useQueryGetMe from '@/api/auth/getMe.ts'
import { DeviceStatus } from '@/api/user/getUsers.ts'
import useQueryGetUserChart from '@/api/user/getUserChart.ts'
import useQueryGetUserChart10Minutes from '@/api/user/getUserChart10Minutes.ts'

export interface IProps {
    id?: number
    className?: string
    onRefresh: () => void
}

const detailSectionGridClassName =
    'member-detail__section-grid grid grid-cols-[repeat(auto-fit,minmax(min(100%,420px),1fr))] gap-x-[14px] gap-y-[14px] md:grid-cols-1'

const UserDetail: React.FC<IProps> = ({ className, id = 0 }) => {
    const { t } = useTranslation()

    const { data: user } = useQueryGetUserDetail(id)
    const { data: recentState } = useQueryGetUserRecentState(id)
    const containerRef = useRef<any>(null)
    const navigate = useNavigate()
    const { data: averageStats } = useQueryGetUserStatAverage(id)
    const { data: me } = useQueryGetMe()
    const [isRealtimeChartActive, setIsRealtimeChartActive] = useState(false)

    // 차트 데이터 API 호출
    const { data: dailyChartData } = useQueryGetUserChart(id)
    const { data: realtimeChartData } = useQueryGetUserChart10Minutes(
        id,
        isRealtimeChartActive
    )

    // 데이터 변환 함수 (string x -> number x)
    const convertToNumericX = (data?: any[]) => {
        if (!data) return undefined
        return data.map((item) => ({
            ...item,
            x: new Date(item.x).getTime() / 1000 // Unix timestamp로 변환
        }))
    }

    // 특정 이메일 계정인지 확인
    const isMaskingRequired = useMemo(() => {
        return me?.email === 'dbd_cm@clomental.com'
    }, [me?.email])

    // 마스킹 처리 함수
    const getMaskedName = (name?: string) => {
        if (!isMaskingRequired || !name) return name
        if (name.length <= 1) return name
        return name.charAt(0) + '**'
    }

    const getMaskedContact = (contact?: string) => {
        if (!isMaskingRequired || !contact) return contact

        // 모든 전화번호 앞 3자리만 보여주고 나머지는 * 처리
        const contactParts = contact.split('-')
        if (contactParts.length === 3) {
            return `${contactParts[0]}-****-****`
        }

        // 기타 패턴의 전화번호 처리
        if (contact.length > 3) {
            return contact.substring(0, 3) + contact.substring(3).replace(/./g, '*')
        }

        return contact
    }

    const getMaskedBirthDate = (birthDate?: string) => {
        if (!isMaskingRequired || !birthDate)
            return birthDate ? format(new Date(birthDate), 'yyyy-MM-dd') : ''
        return `19**-**-**`
    }

    const getMaskedAddress = (address?: string) => {
        if (!isMaskingRequired || !address) return address
        return '****'
    }

    const genderName = useMemo(() => {
        if (user?.gender == 0) return t('common.gender.male')
        if (user?.gender == 1) return t('common.gender.female')
        else return t('common.gender.none')
    }, [user?.gender, t])

    // 실시간 상태를 고려한 실제 디바이스 상태 계산 (SearchUserItem과 동일한 로직)
    const actualDeviceStatus = useMemo(() => {
        if (!user?.device) return undefined

        // 실시간 충전 상태 우선 확인
        if (recentState?.summary?.isCharging) return DeviceStatus.CHARGING

        // 실시간 온라인 상태 확인
        if (recentState?.summary?.isOnline !== undefined) {
            return recentState.summary.isOnline ? DeviceStatus.ONLINE : DeviceStatus.OFFLINE
        }

        // 기본 device.status 사용
        return user.device.status
    }, [user?.device, recentState?.summary?.isCharging, recentState?.summary?.isOnline])

    useEffect(() => {
        containerRef.current.scrollTo(0, 0)
    }, [id])

    const getTime = (minutes?: number) => {
        const pastMinutes = minutes || 0
        const hour = Math.floor(pastMinutes / 60)
        const minute = Math.floor(pastMinutes % 60)
        return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
    }

    return (
        <div className={`${className || ''} member-detail`} ref={containerRef}>
            {user && (
                <>
                    <header className="member-detail__hero">
                        <div className="member-detail__hero-avatar">{getMaskedName(user?.name)?.trim().charAt(0) || '?'}</div>
                        <div className="member-detail__hero-copy">
                            <p>CAREARLY MEMBER PROFILE</p>
                            <h2>{getMaskedName(user?.name)}</h2>
                            <span>{genderName} · {t('users.detail.info.age', { count: calculateAge(user?.birthDate) })} · {t('users.detail.hero.memberNumber', { number: `${format(new Date(user?.createdAt), 'yyyy')}-${user?.id}` })}</span>
                        </div>
                        <div className="member-detail__hero-status">
                            <AlertStatusBadge value={user?.alertStatus} />
                            {user?.device ? (
                                <BatteryStatusBadge
                                    battery={recentState?.summary?.battery ?? user.device.battery}
                                    status={actualDeviceStatus}
                                    showPercentage={true}
                                />
                            ) : <span>{t('users.detail.hero.noDevice')}</span>}
                        </div>
                    </header>
                    <div className="member-detail__overview">
                        <div className="member-detail__main">
                            {/* 첫 번째 행: 회원정보, 사용시간 */}
                            <div className={detailSectionGridClassName}>
                                <div className={'flex flex-col'}>
                                    <p className={'text-left text-[14px] font-bold'}>
                                        {t('users.detail.info.title')}
                                    </p>
                                    <div
                                        className={
                                            'flex-1 bg-white p-[20px] w-full flex flex-col gap-[10px] mt-[10px]'
                                        }
                                    >
                                        <div className={'flex gap-[10px]'}>
                                            <AlertStatusBadge value={user?.alertStatus} />
                                            <p className={'font-bold'}>
                                                {getMaskedName(user?.name)}
                                            </p>
                                        </div>
                                        <p>
                                            {format(new Date(user?.createdAt), 'yyyy')}-{user?.id}
                                        </p>
                                        <p className={''}>
                                            {genderName} /{' '}
                                            {isMaskingRequired
                                                ? getMaskedBirthDate(user?.birthDate)
                                                : format(
                                                      new Date(user?.birthDate),
                                                      'yyyy-MM-dd'
                                                  )}{' '}
                                            (
                                            {t('users.detail.info.age', {
                                                count: calculateAge(user?.birthDate)
                                            })}
                                            )
                                        </p>
                                        <p>
                                            {t('users.detail.info.address')} :{' '}
                                            {getMaskedAddress(
                                                [user?.address, user?.addressDetail]
                                                    .filter(Boolean)
                                                    .join(' ')
                                            ) || t('common.none')}
                                        </p>
                                        <p>
                                            {t('users.detail.info.deviceSerial')} :{' '}
                                            {user?.device?.device_serial || t('common.none')}
                                        </p>
                                    </div>
                                </div>
                                <div className={'flex flex-col'}>
                                    <p className={'text-left text-[14px] font-bold'}>
                                        {t('users.detail.time.title')}
                                    </p>
                                    <div
                                        className={
                                            'flex-1 bg-white p-[20px] w-full mt-[10px] text-[14px] text-center flex flex-col gap-[10px]'
                                        }
                                    >
                                        <div
                                            className={
                                                'w-full grid grid-cols-3 gap-[10px] border border-solid border-transparent border-b border-b-gray2 pb-[10px]'
                                            }
                                        >
                                            <p>{t('users.detail.time.table.period.column')}</p>
                                            <p>{t('users.detail.time.table.powerOn.column')}</p>
                                            <p>{t('users.detail.time.table.wearOn.column')}</p>
                                        </div>
                                        <div className={'w-full grid grid-cols-3'}>
                                            <p>
                                                {t('users.detail.time.table.period.recent24hours')}
                                            </p>
                                            <p>{getTime(averageStats?.devicePowerOnMinute?.day)}</p>
                                            <p>{getTime(averageStats?.deviceWearMinute?.day)}</p>
                                        </div>
                                        <div className={'w-full grid grid-cols-3'}>
                                            <p>{t('users.detail.time.table.period.recent7days')}</p>
                                            <p>
                                                {getTime(averageStats?.devicePowerOnMinute?.week)}
                                            </p>
                                            <p>{getTime(averageStats?.deviceWearMinute?.week)}</p>
                                        </div>
                                        <div className={'w-full grid grid-cols-3'}>
                                            <p>
                                                {t('users.detail.time.table.period.recent30days')}
                                            </p>
                                            <p>
                                                {getTime(averageStats?.devicePowerOnMinute?.month)}
                                            </p>
                                            <p>{getTime(averageStats?.deviceWearMinute?.month)}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* 두 번째 행: 연락처, 주의사항 */}
                            <div className={detailSectionGridClassName}>
                                <div className={'flex flex-col'}>
                                    <p className={'text-left text-[14px] font-bold'}>
                                        {t('users.detail.contact.title')}
                                    </p>
                                    <div
                                        className={
                                            'flex-1 bg-white p-[20px] w-full mt-[10px] flex flex-col gap-[14px]'
                                        }
                                    >
                                        <div className={'flex gap-[20px] md:flex-wrap'}>
                                            <ContactBadge>
                                                {t('users.detail.contact.me')}
                                            </ContactBadge>
                                            <p>
                                                {getMaskedContact(user?.contact) ||
                                                    t('common.none')}
                                            </p>
                                        </div>
                                        <div className={'flex gap-[20px] md:flex-wrap'}>
                                            <ContactBadge>
                                                {t('users.detail.contact.protector')}
                                            </ContactBadge>
                                            <p>
                                                {getMaskedContact(user?.primaryContact) ||
                                                    t('common.none')}
                                            </p>
                                        </div>
                                        <div className={'flex gap-[20px] md:flex-wrap'}>
                                            <ContactBadge>
                                                {t('users.detail.contact.friend')}
                                            </ContactBadge>
                                            <p>
                                                {getMaskedContact(user?.secondaryContact) ||
                                                    t('common.none')}
                                            </p>
                                        </div>
                                        <div className={'flex gap-[20px] md:flex-wrap'}>
                                            <ContactBadge>
                                                {t('users.detail.contact.admin')}
                                            </ContactBadge>
                                            <p>
                                                {getMaskedContact(user?.tertiaryContact) ||
                                                    t('common.none')}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div className={'flex flex-col'}>
                                    <p className={'text-left text-[14px] font-bold'}>
                                        {t('users.detail.memo.title')}
                                    </p>
                                    <div
                                        className={
                                            'flex-1 bg-white p-[20px] w-full mt-[10px] whitespace-pre-wrap'
                                        }
                                    >
                                        {user?.inspectorMemo}
                                    </div>
                                </div>
                            </div>

                        </div>
                        <aside className="member-detail__location-column">
                            <div className="member-detail__location-card">
                                <div
                                    className="member-detail__location-heading"
                                >
                                    <p>
                                        {t('users.detail.location.title')}
                                    </p>
                                    {user?.device && (
                                        <button
                                            className="member-detail__location-more"
                                            onClick={() => navigate(`/user/${id}/map`)}
                                        >
                                            {t('users.detail.location.more')}
                                        </button>
                                    )}
                                </div>
                                <Suspense fallback={null}>
                                    <UserDetailMap
                                        className="member-detail__map"
                                        userID={user?.id}
                                    />
                                </Suspense>
                            </div>
                            <div className="member-detail__device-strip">
                                <p className={'text-white font-bold'}>
                                    {t('users.detail.location.device.status')}
                                </p>
                                {user?.device && recentState?.summary ? (
                                    <div className={'flex items-center gap-2'}>
                                        {/* 모든 상태 값이 미사용 조건일 경우 */}
                                        {recentState.summary.hrate === null &&
                                        recentState.summary.hrateStatus === null &&
                                        recentState.summary.respiration === null &&
                                        recentState.summary.respStatus === null &&
                                        recentState.summary.isCharging === false &&
                                        recentState.summary.isOnline === false &&
                                        recentState.summary.step === 0 &&
                                        (recentState.summary.battery === null ||
                                            recentState.summary.battery === undefined) ? (
                                            <p className={'text-[#ADFF45]'}>
                                                {t('users.detail.location.device.status.notUse')}
                                            </p>
                                        ) : (
                                            <BatteryStatusBadge
                                                battery={recentState.summary.battery || 0}
                                                status={actualDeviceStatus}
                                                showPercentage={true}
                                                className="border-gray-600"
                                            />
                                        )}
                                    </div>
                                ) : (
                                    <p className={'text-[#ADFF45]'}>
                                        {t('users.detail.location.device.status.none')}
                                    </p>
                                )}
                            </div>
                        </aside>
                    </div>

                    {/* SciChart 기반 통합 생체신호 차트 */}
                    <section className="member-detail__biometrics">
                        <BiometricDashboard
                            className={'w-full'}
                            onViewModeChange={(viewMode) =>
                                setIsRealtimeChartActive(viewMode === 'realtime')
                            }
                            heartRateData={convertToNumericX(dailyChartData?.hrate)}
                            respirationData={convertToNumericX(dailyChartData?.respiration)}
                            stepData={convertToNumericX(dailyChartData?.step)}
                            fallData={convertToNumericX(dailyChartData?.fall)}
                            realtimeHeartRate={convertToNumericX(realtimeChartData?.hrate)}
                            realtimeRespiration={convertToNumericX(realtimeChartData?.respiration)}
                            realtimeSteps={convertToNumericX(realtimeChartData?.step)}
                            realtimeFalls={convertToNumericX(realtimeChartData?.fall)}
                        />
                    </section>
                </>
            )}
        </div>
    )
}

export default UserDetail
